// ** React Imports
import { useState } from "react";

// ** MUI Imports
import {
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Grid,
  Tooltip,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MuiCard from "@mui/material/Card";

// ** Icon Imports
import Icon from "./icon";
import AddIcon from "@mui/icons-material/Add";

// ** Custom Component Imports
import CustomAvatar from "components/mui/avatar";
import CustomTextField from "./mui/text-field";
import CustomAutocomplete from "./mui/autocomplete";

// ** Third-Party Imports
import { ToastContainer, toast } from "react-toastify";

const Card = styled(MuiCard)(({ theme }) => ({
  [theme.breakpoints.up("sm")]: { width: "100%" },
}));

const SpecialInstructions = ({ open, handleClose }) => {
  const [isEmpty, setIsEmpty] = useState(true);
  const [instruction, setInstruction] = useState("");
  const [listInstructions, setListInstructions] = useState(false);

  const handleAddInstruction = () => {
    setIsEmpty(false);
  };

  const handleInstructionChange = (value) => {
    setInstruction(value);
  };

  return (
    <Dialog
      fullWidth={true}
      maxWidth="md"
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          minHeight: "40vh",
          minWidth: "40vw",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #D9D9D9",
          paddingBottom: "20px !important",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItem: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h3" fontWeight="bold">
            Special Instructions
          </Typography>
          <IconButton onClick={handleClose}>
            <Icon icon="tabler:x" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={5}>
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                color="main"
                startIcon={<AddIcon />}
                onClick={handleAddInstruction}
              >
                Add Instruction
              </Button>
            </Box>
          </Grid>
          {isEmpty ? (
            <Grid item xs={12}>
              <Card>
                <CardContent
                  sx={{
                    display: "flex",
                    textAlign: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    backgroundColor: "#FBFBFC",
                    p: (theme) => `${theme.spacing(9.75, 5, 9.25)} !important`,
                  }}
                >
                  <CustomAvatar skin="light" color="info" sx={{ mb: 2.25 }}>
                    <Icon icon="tabler:file-description" />
                  </CustomAvatar>
                  <Typography variant="h5" sx={{ mb: 2.75 }}>
                    No Special instructions yet
                  </Typography>
                  <Typography sx={{ mb: 6, color: "text.secondary" }}>
                    Add detailed special instructions specific to both service
                    and sub-service levels
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            <>
              {listInstructions ? (
                <>
                  <Grid item xs={12} md={6}>
                    <Box
                    sx={{
                        border: '1px solid #D0D5DD',
                        borderRadius: 1,
                        px: 4,
                        py: 2,
                    }}
                    >
                        1. {instruction}
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <CustomAutocomplete
                      fullWidth
                      options={[]}
                      getOptionLabel={(option) => option || ""}
                      renderInput={(params) => (
                        <CustomTextField
                          {...params}
                          placeholder="Select Services"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4} md={3}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                        }}
                    >
                        <FormControlLabel
                        sx={{
                            whiteSpace: 'nowrap'
                        }}
                        control={<Checkbox color="success" defaultChecked />}
                        label="Alert On"
                        />
                        <IconButton>
                            <Icon icon='tabler:trash' />
                        </IconButton>
                    </Box>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={7}>
                    <CustomTextField
                      fullWidth
                      placeholder="Add a special instruction..."
                      value={instruction}
                      onChange={(e) => handleInstructionChange(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <CustomAutocomplete
                      fullWidth
                      options={[]}
                      getOptionLabel={(option) => option || ""}
                      renderInput={(params) => (
                        <CustomTextField
                          {...params}
                          placeholder="Select Services"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControlLabel
                      control={<Checkbox color="success" defaultChecked />}
                      label="Alert On"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 2,
                      }}
                    >
                      <Button
                        variant="contained"
                        color="main"
                        disabled={!instruction}
                        onClick={() => setListInstructions(true)}
                      >
                        Save
                      </Button>
                      <Button variant="outlined">Cancel</Button>
                    </Box>
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "20px 24px !important",
          boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
          zIndex: 1,
        }}
        className="dialog-actions-dense"
      >
        <Box>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            Cancel
          </Button>
        </Box>
        <Box>
          <Button variant="contained" color="main" onClick={handleClose}>
            Save Instruction
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SpecialInstructions;
